import { getActiveAccount, getMsalInstance, getScopes } from "../auth/msalConfig.js";
import * as etagCache from "./etag-cache.js";

/**
 * Phase 342 — the portal's authenticated fetcher.
 *
 * <p>Lifted from `apps/operator-console/src/api/fetcher.ts`: acquire a bearer by silent
 * refresh, send it in the `Authorization` header, honour ETags, and on a 401 make
 * exactly ONE forced-refresh retry before declaring the session gone.</p>
 *
 * <h3>Bearer discipline</h3>
 * <p>The token is read, placed in one header, and dropped. It is never logged, never
 * interpolated into a URL, and never attached to a thrown object: {@link ApiError}
 * carries only a status and the response body, {@link AuthExpired} only a reason.
 * A credential that is never written down cannot leak through a console log, a bug
 * report, or an error-reporting integration.</p>
 */

/** No account, or a 401 that survived a forced silent refresh. The gate re-runs and redirects. */
export class AuthExpired extends Error {
  constructor(message = "client-portal: the session has expired") {
    super(message);
    this.name = "AuthExpired";
  }
}

/** Any other non-2xx. The caller decides how to present it. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly bodyText: string,
  ) {
    super(`client-portal: HTTP ${status}`);
    this.name = "ApiError";
  }
}

/**
 * Where requests go.
 *
 * <p>`VITE_ENGINE_URL` is baked in at build time and is what production uses: the portal
 * is a Static Web App and the engine is `api.<env>.ardept.app`, genuinely cross-origin
 * (which is why the engine needs `ARDEPT_PORTAL_ORIGIN` in its CORS policy). In dev the
 * var is unset and Vite proxies `/internal/v0`, so the dev server's own origin is the
 * correct target.</p>
 */
export function engineOrigin(): string {
  return (import.meta.env.VITE_ENGINE_URL as string | undefined) ?? window.location.origin;
}

async function acquireBearer(): Promise<string> {
  const account = getActiveAccount();
  if (!account) throw new AuthExpired("no active account");
  const result = await getMsalInstance().acquireTokenSilent({ account, scopes: getScopes() });
  if (!result?.accessToken) throw new AuthExpired("silent refresh produced no access token");
  return result.accessToken;
}

/**
 * Send an authenticated request to the engine and return its parsed JSON body.
 *
 * @param input an engine path such as `/internal/v0/reporting/families?tenant=…`, or an absolute URL.
 */
export async function fetchWithAuth<T>(input: string, init: RequestInit = {}): Promise<T> {
  const url = input.startsWith("http") ? input : `${engineOrigin()}${input}`;
  const cacheKey = input; // path + querystring — the querystring carries the tenant, so it must be in the key
  const knownEtag = etagCache.getEtag(cacheKey);

  const send = async (bearer: string, withEtag: boolean): Promise<Response> => {
    const headers = new Headers(init.headers as HeadersInit | undefined);
    headers.set("Authorization", `Bearer ${bearer}`);
    if (withEtag && knownEtag) headers.set("If-None-Match", knownEtag);
    if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    return fetch(url, { ...init, headers });
  };

  let bearer = await acquireBearer();
  let res = await send(bearer, true);

  if (res.status === 401) {
    // One forced refresh — MSAL's cached access token may be stale even though the
    // account is fine. A second 401 means the session itself is gone.
    const account = getActiveAccount();
    if (!account) throw new AuthExpired("401 with no active account");
    const result = await getMsalInstance().acquireTokenSilent({
      account,
      scopes: getScopes(),
      forceRefresh: true,
    });
    if (!result?.accessToken) throw new AuthExpired("forced refresh produced no access token");
    bearer = result.accessToken;
    res = await send(bearer, true);
    if (res.status === 401) throw new AuthExpired("second 401 after forced refresh");
  }

  if (res.status === 304) {
    const cached = etagCache.getBody<T>(cacheKey);
    if (cached !== undefined) return cached;
    // The engine says "unchanged" but our memo was cleared underneath us (a reload
    // between the two requests). Ask again WITHOUT the validator rather than showing
    // the client nothing.
    const refresh = await send(bearer, false);
    if (!refresh.ok) throw new ApiError(refresh.status, await refresh.text());
    return capture<T>(cacheKey, refresh);
  }

  if (!res.ok) throw new ApiError(res.status, await res.text());

  return capture<T>(cacheKey, res);
}

async function capture<T>(cacheKey: string, res: Response): Promise<T> {
  const body = (await res.json()) as T;
  const etag = res.headers.get("ETag");
  if (etag) etagCache.store(cacheKey, etag, body);
  return body;
}
